import {
  CompareOrigin,
  trackAddedPlayerToCompare,
} from "@/events/compareMixpanel";
import { useBasePlayerTierAndMetaRating } from "@/hooks/useBasePlayerTierAndMetaRating";
import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import {
  addComparePlayer,
  comparePlayersSelector,
  isInCompareSelector,
  removeComparePlayer,
} from "@repo/shared/redux";
import {
  CardFont,
  DetailedPlayer,
  EvoPathId,
  EvolutionMetaData,
  Foil,
  ObjectiveDisplay,
  Player,
  PriceInfo,
} from "@repo/shared/types";
import { Check, Plus } from "lucide-react";
import { createContext, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

import {
  BasePlayerTierAndMetaRating,
  Tier,
  getRolesPlusOrPlusPlus,
  playerRoleLabelSelector,
  tierSelector,
} from "../../../../redux/players/playersSlice";
import { BREAKPOINTS } from "../../../../settings";
import { PlayerRow } from "./PlayerRow";

export type BasePlayer = {
  possiblePositions: string[];
  rating: number;
  versionId: number;
  metalId: number;
  skillMoves: number;
  weakFoot: number;
  playStyles: number[];
  playStylesPlus?: number[];
  attributes: number[];
  evoPathId?: EvoPathId;
  metaRating: number;
  playerRoleId: number;
};

interface PlayersCardViewProps {
  assetId: number;
  resourceId: number;
  hasDynamicImage: boolean;
  playerUrl: string;
  versionId: number;
  metalId: number;
  name: string;
  cardName: string;
  cardFont?: CardFont;
  foil?: Foil;
  metaRating: number;
  metaRatingDelta?: number;
  metaRatingZeroChem: number;
  playerRoleId: number;
  preferredPosition: string;
  skillMoves: number;
  weakFoot: number;
  desktopCardUrl: string;
  mobileCardUrl: string;
  attributes: number[];
  owned: boolean;
  newPlayer: boolean;
  positions: string[];
  rolesPlus: number[];
  countryId: number;
  leagueId: number;
  clubId: number;
  sbcStorage: boolean;
  rating: number;
  priceInfo?: PriceInfo;
  objectiveDisplay?: ObjectiveDisplay;
  untradeable: boolean;
  isSbcPlayer?: boolean;
  isObjectivePlayer?: boolean;
  onClick?: () => void;
  isMobile?: boolean;
  chemistry?: 0 | 1 | 2 | 3;
  /** Best chemstyle at the sorted chem (from the list response, when known). */
  chemStyleId?: number;
  evolutionPath?: number[];
  pathwayPath?: number[][];
  evoPathId?: EvoPathId;
  playStylesPlus?: number[];
  playStyles?: number[];
  basePlayer?: BasePlayer;
  evolutionBonuses?: {
    skillMovesBonus: boolean;
    weakFootBonus: boolean;
    positions: string[];
    attributes: number[];
    playStyles: number[];
    playStylesPlusBonus?: number[];
  };
  tier?: Tier;
  manuallyChosenPlayerRole?: boolean;
  evoPopularity?: number;
  evolutionData?: EvolutionMetaData;
  inGameAttributes?: { [key: string]: number };
}

export interface PlayersCardViewPropsExpanded extends PlayersCardViewProps {
  isRolesPlusOrPlusPlus: "plus" | "plusPlus";
  playerRoleName: string;
  basePlayerTierAndMetaRating?: BasePlayerTierAndMetaRating;
}

export const PlayersCardView = (props: PlayersCardViewProps) => {
  let tier = useSelector(
    tierSelector(props.metaRating, props.playerRoleId, true),
  );

  const isMobile = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const evolutionBonuses = props.basePlayer && {
    skillMovesBonus: props.basePlayer.skillMoves !== props.skillMoves,
    weakFootBonus: props.basePlayer.weakFoot !== props.weakFoot,
    positions: props.positions.filter(
      (p) => !props.basePlayer.possiblePositions?.includes(p),
    ),
    attributes: props.attributes.map(
      (attr, index) => attr - props.basePlayer?.attributes?.[index],
    ),
    playStyles: props.playStyles?.filter(
      (ps) => !props.basePlayer.playStyles?.includes(ps),
    ),
    playStylesPlusBonus: props.playStylesPlus?.filter(
      (evoPlaystylePlus) =>
        !props.basePlayer?.playStylesPlus?.includes(evoPlaystylePlus),
    ),
  };

  if (props.chemistry === 3) {
    tier = undefined;
  }

  const isRolesPlusOrPlusPlus = getRolesPlusOrPlusPlus({
    playerRoleId: props.playerRoleId,
    rolesPlus: props.rolesPlus,
  });
  const playerRoleName = useSelector(
    playerRoleLabelSelector({
      playerRoleId: props.playerRoleId,
      shortName: true,
    }),
  );

  const basePlayerTierAndMetaRating = useBasePlayerTierAndMetaRating(
    props.basePlayer,
    props.playerRoleId,
    true,
  );

  return (
    <PlayerRow
      {...props}
      isTouchDevice={isMobile}
      tier={tier}
      isRolesPlusOrPlusPlus={isRolesPlusOrPlusPlus}
      playerRoleName={playerRoleName}
      evolutionBonuses={evolutionBonuses}
      basePlayerTierAndMetaRating={basePlayerTierAndMetaRating}
    />
  );
};

/** The player fields the compare toggle stages — subset of a full card row. */
export type CompareTogglePlayer = Pick<
  PlayersCardViewProps,
  | "resourceId"
  | "assetId"
  | "clubId"
  | "leagueId"
  | "countryId"
  | "rating"
  | "preferredPosition"
  | "positions"
  | "playStylesPlus"
  | "playStyles"
  | "hasDynamicImage"
  | "playerUrl"
  | "skillMoves"
  | "weakFoot"
  | "cardName"
  | "cardFont"
  | "foil"
  | "attributes"
  | "versionId"
  | "metalId"
  | "playerRoleId"
  | "metaRating"
  | "priceInfo"
  | "objectiveDisplay"
  | "newPlayer"
  | "evolutionData"
  | "evoPathId"
  | "evolutionPath"
  | "pathwayPath"
  | "inGameAttributes"
  | "chemistry"
  | "chemStyleId"
>;

/**
 * Origin of a card grid that doesn't know its own section — mirrors the same
 * branch PlayerList uses to pick a `view_content` section: an evolution player
 * belongs to Evolutions, everything else to the top-level Players hub.
 */
export const cardCompareOrigin = (player: {
  evolutionPath?: number[];
}): CompareOrigin =>
  player.evolutionPath?.length
    ? { section: "Evolutions", subsection: "Players" }
    : { section: "Players", subsection: "Overview" };

/**
 * Compare-mode wiring shared by the card layouts (and other player grids):
 * active once any player is staged, toggles this card's player in/out of the
 * comparison.
 *
 * `origin` is passed in rather than derived: the hook renders under Players,
 * Evolutions and Squad Builder, and only the caller knows which.
 */
/**
 * The staged Player a compare toggle dispatches. Deep copy: Immer freezes
 * dispatched payloads, and these arrays are shared with the host list's own
 * state. Shared by the card overlay and any other host that stages a player
 * (e.g. the Squad Builder panel toggle), so the field list has one home.
 */
/**
 * The loosest card shape `toCompareTogglePlayer` accepts — hosts whose rows
 * aren't full card props (squad builder, evolution builder) map through it.
 * Optional where any host lacks the field; the mapper fills the gaps.
 */
export type CompareToggleSource = {
  resourceId: number;
  assetId?: number;
  clubId: number;
  leagueId: number;
  countryId: number;
  rating: number;
  preferredPosition: string;
  positions?: string[];
  possiblePositions?: string[];
  playStylesPlus?: CompareTogglePlayer["playStylesPlus"];
  playStyles?: CompareTogglePlayer["playStyles"];
  hasDynamicImage: boolean;
  playerUrl: string;
  skillMoves?: number;
  weakFoot?: number;
  cardName: string;
  cardFont?: CardFont;
  foil?: Foil;
  attributes: number[];
  versionId: number;
  metalId: number;
  playerRoleId: number;
  metaRating?: number;
  metaRatings?: { metaRating: number }[];
  priceInfo?: CompareTogglePlayer["priceInfo"];
  objectiveDisplay?: CompareTogglePlayer["objectiveDisplay"];
  newPlayer?: boolean;
  evolutionData?: CompareTogglePlayer["evolutionData"];
  evoPathId?: CompareTogglePlayer["evoPathId"];
  evolutionPath?: number[];
  pathwayPath?: number[][];
  inGameAttributes?: CompareTogglePlayer["inGameAttributes"];
};

/**
 * Adapt any host's card row to the compare toggle's shape. Chem context
 * (`chemistry`/`chemStyleId`) is deliberately not mapped — its resolution is
 * host-specific, spread it over the result where the site knows it.
 */
export const toCompareTogglePlayer = (
  player: CompareToggleSource,
): CompareTogglePlayer => ({
  resourceId: player.resourceId,
  assetId: player.assetId ?? player.resourceId,
  clubId: player.clubId,
  leagueId: player.leagueId,
  countryId: player.countryId,
  rating: player.rating,
  preferredPosition: player.preferredPosition,
  positions: player.positions ?? player.possiblePositions ?? [],
  playStylesPlus: player.playStylesPlus ?? [],
  playStyles: player.playStyles,
  hasDynamicImage: player.hasDynamicImage,
  playerUrl: player.playerUrl,
  skillMoves: player.skillMoves ?? 0,
  weakFoot: player.weakFoot ?? 0,
  cardName: player.cardName,
  cardFont: player.cardFont,
  foil: player.foil,
  attributes: player.attributes,
  versionId: player.versionId,
  metalId: player.metalId,
  playerRoleId: player.playerRoleId,
  // Hosts without a top-level metaRating carry it as metaRatings[0] (a pitch
  // player may have neither).
  metaRating: player.metaRating ?? player.metaRatings?.[0]?.metaRating ?? 0,
  priceInfo: player.priceInfo,
  objectiveDisplay: player.objectiveDisplay,
  newPlayer: player.newPlayer ?? false,
  evolutionData: player.evolutionData,
  evoPathId: player.evoPathId,
  evolutionPath: player.evolutionPath,
  pathwayPath: player.pathwayPath,
  inGameAttributes: player.inGameAttributes,
});

export const toStagedComparePlayer = (props: CompareTogglePlayer): Player =>
  structuredClone({
    resourceId: props.resourceId,
    assetId: props.assetId,
    clubId: props.clubId,
    leagueId: props.leagueId,
    countryId: props.countryId,
    rating: props.rating,
    preferredPosition: props.preferredPosition,
    positions: props.positions,
    playStylesPlus: props.playStylesPlus ?? [],
    playStyles: props.playStyles,
    hasDynamicImage: props.hasDynamicImage,
    playerUrl: props.playerUrl,
    skillMoves: props.skillMoves,
    weakFoot: props.weakFoot,
    cardName: props.cardName,
    cardFont: props.cardFont,
    foil: props.foil,
    // CompareTogglePlayer has no separate real name; the card name is all we have.
    name: props.cardName,
    attributes: props.attributes,
    versionId: props.versionId,
    metalId: props.metalId,
    playerRoleId: props.playerRoleId,
    metaRating: props.metaRating,
    priceInfo: props.priceInfo,
    objectiveDisplay: props.objectiveDisplay,
    newPlayer: props.newPlayer,
    evolutionData: props.evolutionData,
    evoPathId: props.evoPathId,
    evolutionPath: props.evolutionPath,
    pathwayPath: props.pathwayPath,
    inGameAttributes: props.inGameAttributes,
  }) as Player;

/**
 * Compare mode switched on by the page (the toolbar's Compare button), before
 * any player is staged. Staging a player turns it on regardless.
 */
export const CompareModeContext = createContext(false);

export const useCompareToggle = (
  props: CompareTogglePlayer,
  origin: CompareOrigin,
) => {
  const dispatch = useDispatch();
  const comparePlayers = useSelector(comparePlayersSelector);
  const compareModeOn = useContext(CompareModeContext);
  const compareMode = compareModeOn || comparePlayers.length > 0;
  const isInCompare = useSelector(isInCompareSelector(props));

  const handleToggleCompare = (e: React.MouseEvent) => {
    // The row is wrapped in a link/click handler — block both the default
    // navigation and any ancestor onClick.
    e.preventDefault();
    e.stopPropagation();
    const player = toStagedComparePlayer(props);
    if (isInCompare) {
      dispatch(removeComparePlayer(player));
      return;
    }

    // Stage at the chem context the card was viewed at: the sorted chem plus
    // the row's best chemstyle when the list response carries one.
    dispatch(
      addComparePlayer(player as unknown as DetailedPlayer, {
        chemistry: props.chemistry,
        chemstyleId: props.chemStyleId || undefined,
      }),
    );
    trackAddedPlayerToCompare({
      origin,
      source: "card",
      player_count: comparePlayers.length + 1,
    });
  };

  return { compareMode, isInCompare, handleToggleCompare };
};

/** Overlay toggle shown over the card art while compare mode is active. */
export const CompareToggleButton = ({
  isInCompare,
  onToggle,
  badgeClassName,
}: {
  isInCompare: boolean;
  onToggle: (e: React.MouseEvent) => void;
  /**
   * Extra classes for the round badge. Hosts that render the card inside a
   * CSS-scaled wrapper counter-scale it here so the badge lands at its
   * intended on-screen size (the hit area stays the full card either way).
   */
  badgeClassName?: string;
}) => {
  const { t } = useTranslation("players");
  return (
    <button
      type="button"
      aria-label={
        isInCompare ? t("card.removeFromCompare") : t("card.addToCompare")
      }
      onClick={onToggle}
      // z-40: must sit above SquadBuilderPlayer's z-20 click hitbox (and its
      // z-30 hover chrome), or clicks fall through to the card's own handler.
      className="absolute inset-0 z-40 flex items-center justify-center opacity-85 transition-opacity hover:opacity-100 group-hover:opacity-100"
    >
      <span
        className={cn(
          "flex size-10 items-center justify-center rounded-full border border-neutral-700 bg-neutral-900",
          badgeClassName,
        )}
      >
        {isInCompare ? (
          <Check className="size-5 text-neutral-200" />
        ) : (
          <Plus className="size-5 text-neutral-200" />
        )}
      </span>
    </button>
  );
};
